import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  getDeliveryCompanies,
  getDeliveryCompanyDetail,
  updateDeliveryCompanyAccess,
  setDeliveryCompanyCommissionOverride,
  setDeliveryCompanyActive,
  getDrivers,
  getDistricts,
} from "../../api/adminService";

const COUNTRY_PAIRS = [
  { key: "IL-IL", originCountry: "IL", destinationCountry: "IL", label: "IL → IL" },
  { key: "IL-PS", originCountry: "IL", destinationCountry: "PS", label: "IL → PS" },
  { key: "PS-IL", originCountry: "PS", destinationCountry: "IL", label: "PS → IL" },
  { key: "PS-PS", originCountry: "PS", destinationCountry: "PS", label: "PS → PS" },
];

export default function DeliveryCompanies() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("access");
  const [togglingActive, setTogglingActive] = useState(false);
  const [activeError, setActiveError] = useState("");

  function loadList() {
    setLoading(true);
    getDeliveryCompanies()
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("Could not load delivery companies. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(loadList, []);

  async function openDetail(userId) {
    const data = await getDeliveryCompanyDetail(userId);
    setDetail(data);
    setTab("access");
    setActiveError("");
  }

  async function handleToggleActive() {
    const next = !detail.isCompanyActive;
    if (!next && !window.confirm(`Deactivate ${detail.companyName}? Clients will no longer see this company at checkout.`)) {
      return;
    }
    setTogglingActive(true);
    setActiveError("");
    try {
      const updated = await setDeliveryCompanyActive(detail.userId, next);
      setDetail(updated);
      loadList();
    } catch {
      setActiveError("Could not update the company status.");
    } finally {
      setTogglingActive(false);
    }
  }

  const columns = useMemo(
    () => [
      { field: "companyName", headerName: "Company", flex: 1, minWidth: 200 },
      { field: "driverCount", headerName: "Drivers", width: 100 },
      {
        field: "isActive",
        headerName: "Status",
        width: 130,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? "Active" : "Inactive"}
            color={params.value ? "success" : "default"}
            variant={params.value ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "hasFullAccess",
        headerName: "Access",
        width: 160,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? "Full access" : "Restricted"}
            color={params.value ? "success" : "warning"}
          />
        ),
      },
    ],
    []
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Delivery Companies
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.userId}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          onRowClick={(params) => openDetail(params.row.userId)}
          sx={{ border: "none" }}
        />
      </Box>

      <Drawer anchor="right" open={Boolean(detail)} onClose={() => setDetail(null)}>
        {detail && (
          <Box sx={{ width: 420, p: 3 }}>
            <Typography variant="h6">{detail.companyName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {detail.email}
            </Typography>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
              <Chip
                size="small"
                label={detail.isCompanyActive ? "Active — visible to clients" : "Inactive — hidden from clients"}
                color={detail.isCompanyActive ? "success" : "default"}
                variant={detail.isCompanyActive ? "filled" : "outlined"}
              />
              <Button
                size="small"
                variant="contained"
                color={detail.isCompanyActive ? "error" : "success"}
                disabled={togglingActive}
                onClick={handleToggleActive}
              >
                {detail.isCompanyActive ? "Deactivate" : "Activate"}
              </Button>
            </Stack>
            {activeError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {activeError}
              </Alert>
            )}

            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ my: 2 }}>
              <Tab value="access" label="Access" />
              <Tab value="drivers" label="Drivers" />
              <Tab value="commission" label="Commission" />
            </Tabs>

            {tab === "access" && (
              <AccessForm
                detail={detail}
                onSaved={(updated) => {
                  setDetail(updated);
                  loadList();
                }}
              />
            )}
            {tab === "drivers" && <CompanyDriversTab companyUserId={detail.userId} />}
            {tab === "commission" && (
              <CommissionOverrideForm
                detail={detail}
                onSaved={(updated) => {
                  setDetail(updated);
                  loadList();
                }}
              />
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
}

function AccessForm({ detail, onSaved }) {
  const [mode, setMode] = useState(detail.hasFullAccess ? "full" : "restricted");
  const [selected, setSelected] = useState(
    new Set(detail.allowedPairs.map((p) => `${p.originCountry}-${p.destinationCountry}`))
  );
  const [districts, setDistricts] = useState(detail.allowedDistricts ?? []);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getDistricts()
      .then(setDistrictOptions)
      .catch(() => setDistrictOptions([]))
      .finally(() => setDistrictsLoading(false));
  }, []);

  function togglePair(key) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  async function handleSave() {
    setError("");
    if (mode === "restricted" && selected.size === 0 && districts.length === 0) {
      setError("Select at least one country pair or district, or choose Full Access.");
      return;
    }
    setSaving(true);
    try {
      const pairs = COUNTRY_PAIRS.filter((p) => selected.has(p.key)).map(
        ({ originCountry, destinationCountry }) => ({ originCountry, destinationCountry })
      );
      const updated = await updateDeliveryCompanyAccess(
        detail.userId,
        mode === "full",
        pairs,
        mode === "restricted" ? districts : []
      );
      onSaved(updated);
    } catch {
      setError("Could not save access settings.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
        <FormControlLabel value="full" control={<Radio />} label="Full access (any pickup/dropoff country)" />
        <FormControlLabel value="restricted" control={<Radio />} label="Restricted to specific pairs / districts" />
      </RadioGroup>

      {mode === "restricted" && (
        <>
          <Typography variant="subtitle2">Country pairs</Typography>
          <FormGroup sx={{ pl: 2 }}>
            {COUNTRY_PAIRS.map((p) => (
              <FormControlLabel
                key={p.key}
                control={<Checkbox checked={selected.has(p.key)} onChange={() => togglePair(p.key)} />}
                label={p.label}
              />
            ))}
          </FormGroup>

          <Typography variant="subtitle2">Districts</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
            Grant access to every store in these districts, regardless of the country pairs above -
            drivers will see all new orders picked up from stores there.
          </Typography>
          <Autocomplete
            multiple
            options={districtOptions}
            loading={districtsLoading}
            value={districts}
            onChange={(e, value) => setDistricts(value)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} placeholder={districts.length ? "" : "Select districts"} size="small" />
            )}
          />
        </>
      )}

      <Button variant="contained" disabled={saving} onClick={handleSave}>
        Save Access Settings
      </Button>
    </Stack>
  );
}

function CommissionOverrideForm({ detail, onSaved }) {
  const [value, setValue] = useState(detail.commissionOverridePercent ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      const updated = await setDeliveryCompanyCommissionOverride(detail.userId, value === "" ? null : Number(value));
      onSaved(updated);
    } catch {
      setError("Could not save the commission override.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="body2" color="text.secondary">
        Leave blank to use the platform's global delivery commission rate.
      </Typography>
      <TextField
        label="Custom commission %"
        type="number"
        placeholder="Global rate"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        size="small"
        sx={{ maxWidth: 220 }}
      />
      <Button variant="contained" disabled={saving} onClick={handleSave} sx={{ alignSelf: "flex-start" }}>
        Save
      </Button>
    </Stack>
  );
}

function CompanyDriversTab({ companyUserId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    getDrivers({ deliveryCompanyUserId: companyUserId })
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("Could not load drivers. Is the API running?"))
      .finally(() => setLoading(false));
  }, [companyUserId]);

  const columns = useMemo(
    () => [
      { field: "fullName", headerName: "Name", flex: 1, minWidth: 160 },
      { field: "phone", headerName: "Phone", width: 140, valueGetter: (v) => v || "-" },
      {
        field: "isAvailable",
        headerName: "Available",
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? "Available" : "Offline"}
            color={params.value ? "success" : "default"}
          />
        ),
      },
    ],
    []
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <DataGrid
        autoHeight
        rows={rows}
        getRowId={(row) => row.userId}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        sx={{ border: "none" }}
      />
    </Box>
  );
}
